/**
 * Customer-care panel for a tenant's subscription (ADR-0041 §13). Surfaces the renewal
 * period at-a-glance (countdown + progress + past-due/grace banner), a Stripe Dashboard
 * deep-link + a manual "reconcile from Stripe", the PAYMENT history (Stripe incassi — NOT
 * fiscal invoices; the gestionale emits those), and the subscription-change timeline (from
 * the durable audit). Lives on the platform plane (superadmin / CC).
 *
 * The pure helpers (`daysUntil` / `periodPercent` / `graceDaysLeft` / `timelineLabel`) are
 * unit-tested; the queries + render are integration-tested via the panel.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/api-billing";
import {
  type BillingPaymentsResponse,
  type BillingTimelineEntry,
  type Subscription,
  platformApi,
} from "@/lib/platform";

const DAY_MS = 86_400_000;

/** Whole days from now until `iso` (negative = past). null when absent. */
export function daysUntil(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - now) / DAY_MS);
}

/** Whole days elapsed since `iso` (the dunning anchor). null when absent. */
export function daysSince(iso: string | null, now = Date.now()): number | null {
  if (!iso) return null;
  return Math.floor((now - new Date(iso).getTime()) / DAY_MS);
}

/** Approximate % of the current period elapsed (0–100). We only have `current_period_end`,
 * so the start is derived from the cadence (end − 1 month/year) — a visual gauge, not exact.
 * null when there's no period. */
export function periodPercent(sub: Subscription, now = Date.now()): number | null {
  if (!sub.current_period_end) return null;
  const end = new Date(sub.current_period_end).getTime();
  const start = new Date(sub.current_period_end).getTime();
  const startDate = new Date(start);
  if (sub.billing_period === "year") startDate.setFullYear(startDate.getFullYear() - 1);
  else startDate.setMonth(startDate.getMonth() - 1);
  const span = end - startDate.getTime();
  if (span <= 0) return null;
  return Math.max(0, Math.min(100, ((now - startDate.getTime()) / span) * 100));
}

/** Days of grace left before the dunning tick suspends a past-due tenant. null if not past-due. */
export function graceDaysLeft(sub: Subscription, now = Date.now()): number | null {
  if (sub.status !== "past_due" || !sub.past_due_at) return null;
  const elapsed = daysSince(sub.past_due_at, now) ?? 0;
  return sub.grace_days - elapsed;
}

const TIMELINE_LABELS: Record<string, string> = {
  created: "Abbonamento creato",
  plan_changed: "Cambio piano",
  cancel_scheduled: "Cancellazione programmata (a fine periodo)",
  cancel_resumed: "Cancellazione annullata",
  canceled: "Abbonamento terminato",
  payment_failed: "Pagamento fallito",
  payment_recovered: "Pagamento recuperato",
  suspended: "Sospeso per mancato pagamento",
  reactivated: "Riattivato dopo pagamento",
  comped: "Reso gratuito (comp)",
};

export function timelineLabel(eventType: string): string {
  return TIMELINE_LABELS[eventType] ?? eventType;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

export function SubscriptionCarePanel({ sub, onChanged }: { sub: Subscription; onChanged: () => void }) {
  const tenantId = sub.tenant_id;

  const reconcile = useMutation({
    mutationFn: () => platformApi.reconcileSubscription(tenantId),
    onSuccess: onChanged,
  });

  const paymentsQ = useQuery({
    queryKey: ["platform", "tenants", tenantId, "payments"],
    queryFn: () => platformApi.billingPayments(tenantId),
  });
  const timelineQ = useQuery({
    queryKey: ["platform", "tenants", tenantId, "timeline"],
    queryFn: () => platformApi.billingTimeline(tenantId),
  });

  const daysLeft = daysUntil(sub.current_period_end);
  const pct = periodPercent(sub);
  const grace = graceDaysLeft(sub);

  return (
    <div className="space-y-4">
      {/* Period card — renewal countdown + progress + dunning banner */}
      <div className="rounded-lg border border-border/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm">
            <span className="text-muted-foreground">Periodo di rinnovo</span>
            <div className="mt-0.5 font-medium text-foreground">
              {sub.current_period_end ? (
                <>
                  Si rinnova il {fmtDate(sub.current_period_end)}
                  {daysLeft != null && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {daysLeft >= 0 ? `tra ${daysLeft} gg` : `scaduto da ${-daysLeft} gg`}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">Nessun periodo Stripe</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {sub.stripe_dashboard_url && (
              <a
                href={sub.stripe_dashboard_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-info hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Cliente Stripe
              </a>
            )}
            {sub.stripe_subscription_id && (
              <Button
                size="sm"
                variant="ghost"
                disabled={reconcile.isPending}
                title="Ri-allinea stato e periodo da Stripe"
                onClick={() => reconcile.mutate()}
              >
                <RefreshCw className={`mr-1 h-3.5 w-3.5 ${reconcile.isPending ? "animate-spin" : ""}`} />
                Riconcilia
              </Button>
            )}
          </div>
        </div>
        {pct != null && (
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-elevated">
            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        )}
        {sub.status === "past_due" && (
          <p className="mt-2 text-xs font-medium text-danger">
            Pagamento in ritardo
            {grace != null &&
              (grace > 0
                ? ` · sospensione tra ${grace} gg`
                : " · oltre il periodo di tolleranza")}
          </p>
        )}
        {sub.billing_suspended && (
          <p className="mt-1 text-xs font-medium text-danger">
            Tenant sospeso per mancato pagamento (dunning)
          </p>
        )}
        {sub.cancel_at_period_end && !sub.billing_suspended && (
          <p className="mt-2 text-xs font-medium text-warning">
            In cancellazione
            {sub.current_period_end
              ? ` · resta attivo fino al ${fmtDate(sub.current_period_end)}, poi sospensione`
              : " a fine periodo"}
          </p>
        )}
      </div>

      {/* Payments — Stripe incassi (NOT fiscal invoices) */}
      <div>
        <div className="mb-1.5 flex items-baseline justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Pagamenti
          </h4>
          <span className="text-[11px] text-muted-foreground">
            Incassi Stripe · le fatture sono emesse dal gestionale
          </span>
        </div>
        <PaymentsTable data={paymentsQ.data} loading={paymentsQ.isLoading} />
      </div>

      {/* Timeline — subscription changes (durable audit) */}
      <div>
        <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Cronologia abbonamento
        </h4>
        <ChangeTimeline rows={timelineQ.data ?? []} loading={timelineQ.isLoading} />
      </div>
    </div>
  );
}

function PaymentsTable({ data, loading }: { data: BillingPaymentsResponse | undefined; loading: boolean }) {
  if (loading) return <p className="text-xs text-muted-foreground">Caricamento…</p>;
  if (!data || data.stripe === false)
    return <p className="text-xs text-muted-foreground">Fatturazione Stripe non attiva.</p>;
  if (data.payments.length === 0)
    return <p className="text-xs text-muted-foreground">Nessun pagamento registrato.</p>;
  return (
    <div className="overflow-hidden rounded-lg border border-border/60">
      <table className="w-full text-xs">
        <thead className="bg-elevated/50 text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Data</th>
            <th className="px-2 py-1.5 text-left font-medium">Periodo</th>
            <th className="px-2 py-1.5 text-right font-medium">Importo</th>
            <th className="px-2 py-1.5 text-left font-medium">Stato</th>
          </tr>
        </thead>
        <tbody>
          {data.payments.map((p, i) => (
            <tr key={p.number ?? i} className="border-t border-border/40">
              <td className="px-2 py-1.5 tabular-nums">{fmtDate(p.paid_at ?? p.created)}</td>
              <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                {p.period_start && p.period_end
                  ? `${fmtDate(p.period_start)} → ${fmtDate(p.period_end)}`
                  : "—"}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">
                {formatCurrency(p.total, p.currency)}
              </td>
              <td className="px-2 py-1.5">
                <Badge tone={p.status === "paid" ? "active" : "neutral"}>{p.status ?? "—"}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChangeTimeline({ rows, loading }: { rows: BillingTimelineEntry[]; loading: boolean }) {
  if (loading) return <p className="text-xs text-muted-foreground">Caricamento…</p>;
  if (rows.length === 0)
    return <p className="text-xs text-muted-foreground">Nessun cambio registrato.</p>;
  return (
    <ul className="space-y-1.5">
      {rows.map((r, i) => (
        <li key={i} className="flex items-baseline justify-between gap-3 text-xs">
          <span className="text-foreground">{timelineLabel(r.event_type)}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {new Date(r.occurred_at).toLocaleString()}
          </span>
        </li>
      ))}
    </ul>
  );
}
